import { inject, Injectable } from '@angular/core';
import { FEEDBACK_WIDGET_SELECTOR } from '@core/configs/token.config';
import { User } from '@core/interfaces/user.interface';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { FullNamePipe } from '@shared/pipes/full-name.pipe';
import { NeqtoAuthService } from './neqto-auth.service';

type FeedbackWidget = ReturnType<typeof Sentry.getFeedback>['createForm'] extends (...args: any[]) => infer R
  ? R
  : never;

@Injectable({
  providedIn: 'root',
})
export class FeedbackWidgetService {
  feedbackWidgetSelector = inject(FEEDBACK_WIDGET_SELECTOR, { optional: true }) || '#feedbackWidget';

  #sentryFeedback: ReturnType<typeof Sentry.getFeedback> | null = null;
  #fullNamePipe = new FullNamePipe();
  #triggerButton: HTMLButtonElement | null = null;
  #sentryFeedbackListener: () => void;

  readonly #translateService = inject(TranslateService);
  readonly #authService = inject(NeqtoAuthService);
  readonly #user: User = this.#authService.getUser();

  getSentryFeedback(): ReturnType<typeof Sentry.getFeedback> | null {
    return Sentry.getFeedback();
  }

  createFeedbackWidget(): void {
    this.#triggerButton = document.querySelector<HTMLButtonElement>(this.feedbackWidgetSelector);
    this.#sentryFeedback = this.getSentryFeedback();

    if (this.#sentryFeedback && this.#triggerButton) {
      this.#toggleTriggerButtonVisibility(true);

      this.#sentryFeedbackListener = this.#sentryFeedback.attachTo(this.#triggerButton, {
        triggerLabel: this.#translateService.instant('feedback.trigger.label'),
        formTitle: this.#translateService.instant('feedback.form.title'),
        submitButtonLabel: this.#translateService.instant('feedback.submit.button.label'),
        cancelButtonLabel: this.#translateService.instant('feedback.cancel.button.label'),
        confirmButtonLabel: this.#translateService.instant('feedback.confirm.button.label'),
        emailLabel: this.#translateService.instant('feedback.email.label'),
        emailPlaceholder: this.#translateService.instant('feedback.email.placeholder'),
        messageLabel: this.#translateService.instant('feedback.message.label'),
        messagePlaceholder: this.#translateService.instant('feedback.message.placeholder'),
        nameLabel: this.#translateService.instant('feedback.name.label'),
        namePlaceholder: this.#translateService.instant('feedback.name.placeholder'),
        successMessageText: this.#translateService.instant('feedback.success.message'),
        isRequiredLabel: this.#translateService.instant('feedback.is-required.label'),
        addScreenshotButtonLabel: this.#translateService.instant('feedback.add-screenshot.label'),
        removeScreenshotButtonLabel: this.#translateService.instant('feedback.remove-screenshot.label'),
        onFormOpen: () => {
          this.#toggleTriggerButtonVisibility(false);

          const dialog = document.querySelector<HTMLDialogElement>('#sentry-feedback');
          const nameInput = dialog.shadowRoot.querySelector<HTMLInputElement>('.form__input[name="name"]');
          const emailInput = dialog.shadowRoot.querySelector<HTMLInputElement>('.form__input[name="email"]');
          const fullName = this.#fullNamePipe.transform(this.#user, ['firstname', 'surname_prefix', 'surname']);

          if (nameInput && emailInput) {
            nameInput.value = fullName || '';
            emailInput.value = this.#user.email || '';
          }
        },
        onFormClose: () => {
          this.#toggleTriggerButtonVisibility(true);
        },
        onFormSubmitted: () => {
          this.#toggleTriggerButtonVisibility(true);
        },
      });
    }
  }

  removeFeedbackWidget(): void {
    if (this.#sentryFeedback) {
      this.#sentryFeedbackListener();
      this.#sentryFeedback.remove();
      this.#sentryFeedback = null;
      document.querySelector('#sentry-feedback')?.remove();
    }
  }

  reloadFeedbackWidget(): void {
    if (this.#sentryFeedback) {
      this.removeFeedbackWidget();
      this.createFeedbackWidget();
    }
  }

  #toggleTriggerButtonVisibility(visible: boolean): void {
    this.#triggerButton.ariaHidden = visible ? 'false' : 'true';
  }
}
